import React from 'react';
import './Subprocessors.css';

const Subprocessors: React.FC = () => {
  return (
    <div className="subprocessors-container">
      <div className="subprocessors-content">
        <header className="subprocessors-header">
          <div className="subprocessors-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L2 7L12 12L22 7L12 2Z" fill="#667eea"/>
              <path d="M2 17L12 22L22 17" stroke="#667eea" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 12L12 17L22 12" stroke="#667eea" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h1 className="subprocessors-title">Subprocessors</h1>
          <div className="subprocessors-meta">
            <p><strong>URL:</strong> <code>https://soundlegal.ai/subprocessors</code></p>
            <p><strong>Last updated:</strong> 09/01/2025</p>
          </div>
        </header>

        <div className="subprocessors-body">
          <section className="subprocessors-intro">
            <p>
              We use a small set of trusted <strong>subprocessors</strong> to deliver the Service. Each is vetted for security and privacy, bound by a data processing agreement, and limited to the <strong>minimum data necessary</strong> for its task.
            </p>
          </section>

          <section className="subprocessors-section">
            <h2>Current Subprocessors</h2>
            <div className="subprocessors-table-container">
              <table className="subprocessors-table">
                <thead>
                  <tr>
                    <th>Subprocessor</th>
                    <th>Purpose</th>
                    <th>Data Categories Processed</th>
                    <th>Primary Location</th>
                    <th>Transfer Safeguards</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Google Cloud Platform (Google LLC)</strong></td>
                    <td>Hosting, storage, networking, backups, logging</td>
                    <td>Uploads, Outputs, account & usage metadata</td>
                    <td>United States (regionalized)</td>
                    <td>SCCs (where applicable), enterprise security program</td>
                  </tr>
                  <tr>
                    <td><strong>Google Vertex AI / Gemini (Google LLC)</strong></td>
                    <td>Model inference for analysis/generation</td>
                    <td>Text supplied for inference; derived tokens/embeddings</td>
                    <td>United States (via GCP)</td>
                    <td>SCCs (where applicable), in-flight/at-rest encryption</td>
                  </tr>
                  <tr>
                    <td><strong>Stripe, Inc.</strong></td>
                    <td>Payment processing & billing</td>
                    <td>Payment tokens, billing name/email, invoice metadata</td>
                    <td>United States (with global infra)</td>
                    <td>PCI-DSS; SCCs (where applicable)</td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <div className="subprocessors-note">
              <blockquote>
                We do not permit subprocessors to use your data for their own purposes. Access is role-limited and logged.
              </blockquote>
            </div>
          </section>

          <section className="subprocessors-section">
            <h2>Selection, Vetting, and Oversight</h2>
            <ul>
              <li><strong>Security due diligence:</strong> certifications/reports (e.g., SOC 2/ISO 27001), pen-test posture, breach history.</li>
              <li><strong>Contractual safeguards:</strong> DPAs with confidentiality, technical/organizational measures, incident notice obligations, and <strong>no secondary use</strong>.</li>
              <li><strong>Ongoing review:</strong> annual reassessments, change monitoring, and minimum-privilege access scoping.</li>
            </ul>
          </section>

          <section className="subprocessors-section">
            <h2>Updates & Your Options</h2>
            <ul>
              <li><strong>Advance notice:</strong> We will post changes here and notify admins <strong>in-product or by email</strong> before a new subprocessor gains access to personal data (except emergencies; then we notify promptly after).</li>
              <li><strong>Objections:</strong> If you have a reasonable data-protection objection to a new subprocessor, email <strong><a href="mailto:info@soundlegal.ai">info@soundlegal.ai</a></strong> within <strong>15 days</strong>. We will work in good faith on alternatives; if unresolved, you may terminate the affected Service for a <strong>pro-rata refund</strong> of prepaid, unused fees as set out in our DPA.</li>
              <li><strong>Subscribe to updates:</strong> Email <strong><a href="mailto:info@soundlegal.ai">info@soundlegal.ai</a></strong> with subject "Subprocessor Updates".</li>
            </ul>
          </section>

          <hr className="subprocessors-divider" />

          <section className="subprocessors-section subprocessors-contact">
            <h2>Contact & Support</h2>
            <div className="contact-grid">
              <div className="contact-item">
                <h3>General Inquiries</h3>
                <p><strong>Email:</strong> <a href="mailto:info@soundlegal.ai">info@soundlegal.ai</a></p>
                <p><strong>Response Time:</strong> Within 24-48 hours</p>
              </div>
              <div className="contact-item">
                <h3>Subprocessor Objections</h3>
                <p><strong>Email:</strong> <a href="mailto:info@soundlegal.ai">info@soundlegal.ai</a></p>
                <p><strong>Subject Line:</strong> "Subprocessor Objection"</p>
                <p><strong>Timeline:</strong> Within 15 days of notification</p>
              </div>
              <div className="contact-item">
                <h3>Update Subscriptions</h3>
                <p><strong>Email:</strong> <a href="mailto:info@soundlegal.ai">info@soundlegal.ai</a></p>
                <p><strong>Subject Line:</strong> "Subprocessor Updates"</p>
                <p><strong>For:</strong> Email notifications of changes</p>
              </div>
            </div>
            <div className="subprocessors-footer-note">
              <p><strong>Important:</strong> All subprocessor changes are carefully vetted and communicated in advance. We maintain strict data protection standards across all our service providers.</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Subprocessors;
